import { trackEvent } from '@/common/store/pinia/telemetry/core/client';
import { bytesToMB, toSeconds, assignIfFinite } from '@/common/store/pinia/telemetry/core/utils';
import type { MemoryInfo, DomContentLoadedTiming, FullyLoadedTiming } from '@/common/store/pinia/telemetry/core/types';

export const trackMemoryUsage = (
  memoryInfo: MemoryInfo,
  additionalProperties?: Record<string, unknown>
): void => {
  const usagePercentage = memoryInfo.usedJSHeapSize && memoryInfo.jsHeapSizeLimit
    ? Math.round((memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100)
    : null;

  const properties = {
    used_heap_size_mb: bytesToMB(memoryInfo.usedJSHeapSize),
    total_heap_size_mb: bytesToMB(memoryInfo.totalJSHeapSize),
    heap_size_limit_mb: bytesToMB(memoryInfo.jsHeapSizeLimit),
    usage_percentage: usagePercentage,
    ...additionalProperties
  };

  trackEvent('memory_usage_tracked', properties);
};

const getMemoryInfo = (): MemoryInfo | null => {
  try {
    if (!performance.memory) {
      return null;
    }

    const memoryInfo: MemoryInfo = {
      usedJSHeapSize: performance.memory.usedJSHeapSize,
      totalJSHeapSize: performance.memory.totalJSHeapSize,
      jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
      timestamp: Date.now()
    };

    return memoryInfo;
  } catch (error) {
    return null;
  }
};

export const trackMemorySnapshot = (additionalProperties?: Record<string, unknown>): void => {
  try {
    const memoryInfo = getMemoryInfo();

    if (!memoryInfo) {
      return;
    }

    trackMemoryUsage(memoryInfo, additionalProperties);
  } catch (error) {
    // Silently fail memory tracking to avoid affecting app performance
  }
};

export const trackDomContentLoaded = (
  timing: DomContentLoadedTiming,
  additionalProperties?: Record<string, unknown>
): void => {
  const properties: Record<string, unknown> = {
    dom_content_loaded_ms: Math.round(timing.duration),
    dom_content_loaded_seconds: toSeconds(timing.duration),
    ...additionalProperties
  };

  assignIfFinite(properties, 'dom_content_loaded_start', timing.domContentLoadedStart);
  assignIfFinite(properties, 'dom_content_loaded_end', timing.domContentLoadedEnd);
  assignIfFinite(properties, 'navigation_start', timing.navigationStart);

  trackEvent('dom_content_loaded', properties);
};

export const trackFullyLoadedTime = (
  timing: FullyLoadedTiming,
  additionalProperties?: Record<string, unknown>
): void => {
  const properties: Record<string, unknown> = {
    fully_loaded_ms: Math.round(timing.duration),
    fully_loaded_seconds: toSeconds(timing.duration),
    ...additionalProperties
  };

  assignIfFinite(properties, 'load_event_start', timing.loadEventStart);
  assignIfFinite(properties, 'load_event_end', timing.loadEventEnd);
  assignIfFinite(properties, 'navigation_start', timing.navigationStart);

  trackEvent('page_fully_loaded', properties);
};
